import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	ScheduledReport,
	ScheduledReportsDrawerType,
	TimeRange,
	FrequencyMonthly,
	Frequency,
	ReportFormat,
} from 'src/types/scheduledReportTypes';
import { DateTime } from 'luxon';
import { fetchScheduledReports } from './thunks/fetchScheduledReports';
import { convertScheduledReportToLocalTime } from 'src/routes/scheduled-reports/helpers';

export interface SchedulingState {
	isScheduleDrawerOpen: boolean;
	drawerMode: 'create' | 'edit';
	scheduleDrawerData: ScheduledReportsDrawerType;
	scheduledReports: ScheduledReport[];
	loading: boolean;
	error: string | null;
	drawerContext: 'general' | 'report';
	contextReportId?: string | null | undefined;
	toast: {
		message: string;
		type: 'success' | 'info' | 'warning' | 'error' | 'default';
	};
}

const initialDrawerState: ScheduledReportsDrawerType = {
	enabled: true,
	format: ReportFormat.PDF,
	frequency: Frequency.daily,
	frequencyDayOfMonth: 1,
	frequencyDayOfWeek: 0,
	frequencyMonthly: FrequencyMonthly.first,
	frequencyTime: '19:00',
	isReportNameFixed: false,
	lastModifiedOn: '',
	recipients: [],
	recipientsError: '',
	relativeDateRange: TimeRange.last7Days,
	reportName: '',
	reportNameError: '',
	reportId: '',
	zoneId: DateTime.local().zoneName,
	scheduledId: '',
};

const initialState: SchedulingState = {
	isScheduleDrawerOpen: false,
	drawerMode: 'create',
	scheduleDrawerData: { ...initialDrawerState },
	scheduledReports: [],
	loading: false,
	error: null,
	drawerContext: 'general',
	contextReportId: undefined,
	toast: {
		message: '',
		type: 'success',
	},
};

const schedulingSlice = createSlice({
	name: 'scheduling',
	initialState,
	reducers: {
		resetSchedulingState: () => ({
			...initialState,
			scheduleDrawerData: { ...initialDrawerState },
		}),
		openScheduleDrawerForCreate: (
			state,
			action: PayloadAction<{
				context?: 'general' | 'report';
				reportId?: string;
			}>,
		) => {
			state.isScheduleDrawerOpen = true;
			state.drawerMode = 'create';
			state.scheduleDrawerData = { ...initialDrawerState };
			state.drawerContext = action.payload?.context || 'general';
			state.contextReportId = action.payload?.reportId;
		},
		openScheduleDrawerForEdit: (
			state,
			action: PayloadAction<ScheduledReport>,
		) => {
			state.isScheduleDrawerOpen = true;
			state.drawerMode = 'edit';
			const report = action.payload;
			state.scheduleDrawerData = {
				enabled: report.enabled,
				format: report.format,
				frequency: report.frequency,
				frequencyDayOfMonth: report.frequencyDayOfMonth,
				frequencyDayOfWeek: report.frequencyDayOfWeek,
				frequencyMonthly: report.frequencyMonthly,
				frequencyTime: report.frequencyTime,
				isReportNameFixed: true,
				lastModifiedOn: report.lastModifiedOn,
				recipients: report.recipients,
				recipientsError: '',
				relativeDateRange: report.relativeDateRange,
				reportName: report.reportName,
				reportNameError: '',
				reportId: report.reportId,
				zoneId: report.zoneId,
				scheduledId: report.id,
			};
		},
		closeScheduleDrawer: (state) => {
			state.isScheduleDrawerOpen = false;
			state.drawerMode = 'create';
			state.scheduleDrawerData = { ...initialDrawerState };
			state.drawerContext = 'general';
			state.contextReportId = undefined;
			state.error = null;
			state.loading = false;
		},
		updateScheduleDrawerField: (
			state,
			action: PayloadAction<{
				field: keyof ScheduledReportsDrawerType;
				value: any;
			}>,
		) => {
			const { field, value } = action.payload;
			(state.scheduleDrawerData as any)[field] = value;
		},
		updateScheduleDrawerReport: (
			state,
			action: PayloadAction<{ reportId: string; reportName: string }>,
		) => {
			state.scheduleDrawerData.reportId = action.payload.reportId;
			state.scheduleDrawerData.reportName = action.payload.reportName;
			state.scheduleDrawerData.reportNameError = '';
		},
		setRecipientsError: (state, action: PayloadAction<string>) => {
			state.scheduleDrawerData.recipientsError = action.payload;
		},
		setReportNameError: (state, action: PayloadAction<string>) => {
			state.scheduleDrawerData.reportNameError = action.payload;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				message: string;
				type: 'info' | 'success' | 'error' | 'warning' | 'default';
			}>,
		) => {
			state.toast = { ...state.toast, ...action.payload };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchScheduledReports.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchScheduledReports.fulfilled,
				(state, action: PayloadAction<ScheduledReport[]>) => {
					state.loading = false;
					state.scheduledReports = action.payload.map(convertScheduledReportToLocalTime);
					state.error = null;
				},
			)
			.addCase(
				fetchScheduledReports.rejected,
				(state, action: PayloadAction<string>) => {
					state.loading = false;
					state.error = action.payload as string;
				},
			);
	},
});

export const {
	openScheduleDrawerForCreate,
	openScheduleDrawerForEdit,
	closeScheduleDrawer,
	updateScheduleDrawerField,
	updateScheduleDrawerReport,
	setRecipientsError,
	setReportNameError,
	pushToast,
	resetSchedulingState,
} = schedulingSlice.actions;
export default schedulingSlice.reducer;
